import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CustomPageType } from '../custom-pages/cp-enum';
import { CpRouterDatabuilderService } from '../custom-pages/services/cp-router-databuilder.service';
import { ICustomPageMasterDataResolver } from '../custom-pages/services/master-data';
import { HeaderService } from '../header-information/header-information.service';
import { CpExamination, CpRouterData, CustomPageConfig } from '../models/custom-page';
import { fromServerIsoString } from '../utils/interop';
import { VisitExamination, VisitPatient } from '../models/visit';
import { SLIDER_OPTIONS } from '../se-toolbar/se-buttons-toolbar/redux/se-buttons-toolbar.state';
import { MainToolbarHeaderInfo } from '../se-toolbar/se-header-toolbar/se-header-info/se-header-info.component';
import { ReportWorkflowService } from './report-workflow.service';
import { IReportWorkflowExamService } from './report-workflow-exam.service';
import { EstensaWaitingService } from './waiting.service';
import { ExaminationWorkflowDTO, PatientWorkflowDTO } from '../models';

export interface CpParamsOptions {
    examinations: CpExamination[];
    addExaminationsToReport: boolean;
    customPageType?: CustomPageType;
    services: {
        masterDataResolver: ICustomPageMasterDataResolver;
        examService: IReportWorkflowExamService;
        reportWorkflowService: ReportWorkflowService;
    };
    isFromThirdPart?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class ReportingDataBuilder {
    onBack?: (data: any) => Promise<unknown> | undefined;

    private masterDataResolver!: ICustomPageMasterDataResolver;
    private examService!: IReportWorkflowExamService;
    private reportWorkflowService!: ReportWorkflowService;

    constructor(
        private cpRouterDatabuilder: CpRouterDatabuilderService,
        private headerService: HeaderService,
        private translate: TranslateService,
        private waitingService: EstensaWaitingService,
    ) {}

    getMasterDataResolver() {
        return this.masterDataResolver;
    }

    getExamService() {
        return this.examService;
    }

    getReportWorkflowService() {
        return this.reportWorkflowService;
    }

    setServices(
        masterDataResolver: ICustomPageMasterDataResolver,
        examService: IReportWorkflowExamService,
        reportWorkflowService: ReportWorkflowService,
    ) {
        this.masterDataResolver = masterDataResolver;
        this.examService = examService;
        this.reportWorkflowService = reportWorkflowService;
    }

    public async prepareCpParams(options: CpParamsOptions) {
        this.waitingService.isWaiting = true;
        let cpArguments: CpRouterData;
        try {
            const args = await this.cpRouterDatabuilder.buildCpArguments(
                options.examinations,
                options.services.examService,
                options.addExaminationsToReport,
            );

            if (!args || !args.idCustomPageRoot) {
                return;
            }

            cpArguments = { ...args };
        } finally {
            this.waitingService.isWaiting = false;
        }

        let patient = options.examinations[0].patient;
        let descriptions: string[] = [];
        let examinationDate = null;
        options.examinations.forEach((item) => {
            if (item.isEcg) {
                descriptions.push(item.exam.description);
            } else {
                // isVisit
                descriptions.unshift(item.exam.description);
            }
            examinationDate = item.examinationDate
                ? fromServerIsoString(item.examinationDate)
                : fromServerIsoString(item.reportDate);
            examinationDate = examinationDate
                ?.toDate()
                .toLocaleDateString(undefined, { hour: '2-digit', minute: '2-digit' });
            if (!item.patient.isLocalPatient) {
                patient = item.patient;
            }
        });
        let procedureInfoDescription = descriptions.join('+');

        const name = patient.firstName || patient?.name;
        const surname = patient.lastName || patient?.surname;

        const patientInfo: MainToolbarHeaderInfo = {
            mainDescription: surname + ' ' + name + (!!patient.sexCode ? ' (' + patient.sexCode + ')' : ''),
            secondaryDescription:
                this.translate.instant('BirthDate') + ': ' + new Date(patient.birthDate).toLocaleDateString(),
            infoList: [],
        };
        const procedureInfo: MainToolbarHeaderInfo = {
            mainDescription: procedureInfoDescription,
            infoList: [],
            secondaryDescription: this.translate.instant('ExamDate') + ': ' + examinationDate || '',
        };

        if (options.customPageType === undefined) {
            options.customPageType = options.examinations[0].isUs
                ? CustomPageType.ULTRASOUND
                : CustomPageType.CARDIOLOGY_VISIT;
        }

        const toolbar = {
            config: new CustomPageConfig(
                options.customPageType,
                true,
                SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
                SLIDER_OPTIONS.RIGHT_SLIDER_DISABLED,
            ),
            info1: procedureInfo,
            info2: patientInfo,
        };

        this.setServices(
            options.services.masterDataResolver,
            options.services.examService,
            options.services.reportWorkflowService,
        );

        return { ...cpArguments, toolbar, isFromThirdPart: options?.isFromThirdPart };
    }

    public prepareECGParams(examinations: VisitExamination[]) {
        this.headerService.clear();
        const exams = examinations.map((item) => {

            return {
                examinationId: item.id,
                reportId: item.idReport,
                patient: item.patient,
                examinationDate: (item.examinationDate != null) ? fromServerIsoString(item.examinationDate).toDate() : null,
                isEcgReport: item.isEcgReport, //
                isReported: item.isReported, //
                withEcg: true,
                reportingWithoutTrace: item.reportingWithoutTrace //
            };
        });

        return { exams };
    }

    public prepareECGParamsEx(examinations: ExaminationWorkflowDTO[], isFromThirdPart: boolean = false) {
        this.headerService.clear();
        const exams = examinations.map((item) => {

            return {
                examinationId: item.id,
                reportId: item.idReport,
                patient: toVisitPatient(item.patient),
                //examinationDate: (item.examinationDate != null) ? fromServerIsoString(item.examinationDate).toDate() : null,
                examinationDate: (item.admissionDate != null) ? fromServerIsoString(item.admissionDate).toDate() : null, 
                isEcgReport: item.isEcgReport, 
                isReported: item.isReported, 
                withEcg: true,
                reportingWithoutTrace: item.reportingWithoutTrace
            };
        });

        return { exams, isFromThirdPart: isFromThirdPart };
    }
}

function toVisitPatient(patient: PatientWorkflowDTO): VisitPatient {
    return  {
        id: patient.id,
        patientId: patient.patientId,
        otherPatientId: patient.otherPatientId,
        lastName: patient.surname ?? null,
        firstName: patient.name ?? null,
        birthDate: patient.birthDate ?? null,
        taxCode: patient.taxCode ?? null,
        sexCode: patient.sex?.code,
        sex: patient.sex?.code,

        idLocalPatient: null,
        isLocalPatient: false,
        isCertified: undefined,
    }
}
